/**
 * Mail Address Settings - Display of send-from address (read-only) and test connection for Graph API.
 * Changing the sender address requires contacting the system developer.
 */
import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../../config';
import { useSecretExpiry } from '../../../../contexts/SecretExpiryContext';
import './mailAddressSettings.css';

const MailAddressSettings: React.FC = () => {
  const [testMessage, setTestMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [fromAddress, setFromAddress] = useState<string>('noreply@saigyo.com');
  const secretExpiry = useSecretExpiry();

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/mail-from`)
      .then((res) => res.json())
      .then((data: { fromAddress?: string | null }) => {
        const addr = data?.fromAddress;
        if (addr && typeof addr === 'string' && addr.trim()) {
          setFromAddress(addr.trim());
        }
      })
      .catch(() => {});
  }, []);

  const handleTestConnection = () => {
    setTestMessage(null);
    setTestLoading(true);
    fetch(`${API_BASE_URL}/api/graph/send-test-email`, { method: 'POST' })
      .then((res) => res.json())
      .then((data: { success?: boolean; message?: string }) => {
        setTestMessage({
          type: data.success ? 'success' : 'error',
          text: data.message ?? (data.success ? 'テストメールを送信しました。' : '送信に失敗しました。'),
        });
      })
      .catch(() => setTestMessage({ type: 'error', text: 'テストメールの送信に失敗しました。' }))
      .finally(() => setTestLoading(false));
  };

  return (
    <div className="mail-address-settings">
      <div className="mail-address-settings__intro">
        <p>
          本システムのメール送信には <strong>Microsoft Graph API</strong>（Azure Entra ID）を利用しています。
        </p>
        <p>
          下のボタンで接続状態を確認できます。
        </p>
      </div>

      <div className="mail-address-settings__test-section">
        <button
          type="button"
          className="mail-address-settings__btn-test"
          onClick={handleTestConnection}
          disabled={testLoading}
        >
          {testLoading ? '接続確認中...' : 'テスト接続'}
        </button>
      </div>

      {testMessage && (
        <p className={testMessage.type === 'success' ? 'mail-address-settings__success' : 'mail-address-settings__error'}>
          {testMessage.text}
        </p>
      )}

      <div className="mail-address-settings__domain-section">
        <p className="mail-address-settings__domain-label">
          送信元メールアドレス
          <br />
          送信元として利用するメールアドレスを変更する場合は、システム開発者にお問い合わせください。（同じドメインをご利用ください。）
        </p>
        <div className="mail-address-settings__email-row">
          <div className="mail-address-settings__email-display">
            <input
              type="text"
              className="mail-address-settings__input mail-address-settings__input--local"
              value={fromAddress}
              readOnly
              disabled
              aria-readonly
            />
          </div>
        </div>
      </div>

      <div className="mail-address-settings__notes">
        <p>ドメインの変更をご希望の場合は、アプリケーション開発者にお問い合わせください。</p>
        <p>シークレットキーの更新期限の警告が表示された場合は、アプリケーション開発者にお知らせください。</p>
      </div>

      {!secretExpiry.loading && secretExpiry.warning && secretExpiry.expiresAt && (
        <div
          className={`mail-address-settings__secret-warning mail-address-settings__secret-warning--${secretExpiry.severity}`}
          role="alert"
        >
          <p className="mail-address-settings__secret-warning-text">
            {secretExpiry.severity === 'critical'
              ? `クライアントシークレットの有効期限が切れています（${secretExpiry.expiresAt}）。至急、アプリケーション開発者にお知らせください。`
              : secretExpiry.severity === 'urgent'
                ? `クライアントシークレットの有効期限がまもなくです（${secretExpiry.expiresAt}）。更新の際はアプリケーション開発者にお知らせください。`
                : `クライアントシークレットの有効期限が近づいています（${secretExpiry.expiresAt}）。更新の際はアプリケーション開発者にお知らせください。`}
          </p>
        </div>
      )}
    </div>
  );
};

export default MailAddressSettings;
